import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Users, Settings, Sun, FileText, Mail, Check, Megaphone } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { gmailStatus, connectGmail } from "../lib/gmail";
import SettingsDialog from "./SettingsDialog";

export default function Layout() {
  const { user, logout } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [gmail, setGmail] = useState({ configured: false, connected: false, email: "" });
  const location = useLocation();
  const navigate = useNavigate();

  const refreshGmail = () => gmailStatus().then(setGmail).catch(() => {});
  useEffect(() => { refreshGmail(); }, []);
  useEffect(() => {
    const g = new URLSearchParams(location.search).get("gmail");
    if (g === "connected") { toast.success("Gmail connected — you can now send emails from PIT"); refreshGmail(); navigate(location.pathname, { replace: true }); }
    else if (g === "error") { const reason = new URLSearchParams(location.search).get("reason"); toast.error(reason ? `Gmail connection failed: ${decodeURIComponent(reason)}` : "Gmail connection failed. Please try again."); navigate(location.pathname, { replace: true }); }
    else if (g === "notconfigured") { toast.error("Gmail sending isn't set up on this environment yet."); navigate(location.pathname, { replace: true }); }
  }, [location.search]);

  const navCls = ({ isActive }) =>
    `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
      isActive ? "bg-[#4690d9] text-white shadow-[0_0_16px_rgba(70,144,217,0.35)]" : "text-slate-400 hover:text-white hover:bg-white/5"
    }`;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0a1830]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex flex-col leading-none" data-testid="app-logo">
              <span className="text-xl font-extrabold tracking-tight font-heading">
                <span className="text-[#9cc6f0]">Growth</span>
                <span className="text-white">OS</span>
                <span className="text-slate-500 font-semibold text-xs ml-1.5">PIT</span>
              </span>
            </div>
            <nav className="flex items-center gap-1">
              <NavLink to="/today" className={navCls} data-testid="nav-today">
                <Sun size={15} /> Today
              </NavLink>
              <NavLink to="/dashboard" className={navCls} data-testid="nav-dashboard">
                <LayoutDashboard size={15} /> Dashboard
              </NavLink>
              <NavLink to="/prospects" className={navCls} data-testid="nav-prospects">
                <Users size={15} /> Prospects
              </NavLink>
              <NavLink to="/form" className={navCls} data-testid="nav-form">
                <FileText size={15} /> Form
              </NavLink>
              <NavLink to="/campaigns" className={navCls} data-testid="nav-campaigns">
                <Megaphone size={15} /> Campaigns
              </NavLink>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {gmail.configured && (gmail.connected ? (
              gmail.needs_reconnect ? (
                <button
                  onClick={connectGmail}
                  data-testid="reconnect-gmail-btn"
                  title="Reconnect Gmail to enable reply detection"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-xs font-semibold transition-colors"
                >
                  <Mail size={13} /> Reconnect for replies
                </button>
              ) : (
                <span className="status-pill hidden sm:inline-flex text-[10px] text-emerald-300 border-emerald-500/30" title={`Sending as ${gmail.email}`} data-testid="gmail-connected-pill">
                  <Check size={11} /> Gmail
                </span>
              )
            ) : (
              <button
                onClick={connectGmail}
                data-testid="connect-gmail-btn"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#4690d9]/40 text-[#9cc6f0] hover:bg-[#4690d9]/10 text-xs font-semibold transition-colors"
              >
                <Mail size={13} /> Connect Gmail
              </button>
            ))}
            <span className="status-pill hidden sm:inline-flex text-[10px]">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
              Online
            </span>
            <button
              data-testid="open-settings-btn"
              onClick={() => setSettingsOpen(true)}
              className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
              title="Integrations & settings"
            >
              <Settings size={17} />
            </button>
            {user?.picture && (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-[#4690d9]/40" data-testid="user-avatar" />
            )}
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="p-2 rounded-full text-slate-400 hover:text-rose-400 hover:bg-white/5 transition-colors"
              title="Sign out"
            >
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Outlet />
      </main>
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
